import { addToCart, getProductDetail, getSingleProductPrices } from '@/actions/ecom/productDetail';
import PageLayout from '@/components/Layouts/PageLayout';
import Breadcrumb from '@/shared/crisp/Breadcrumb';
import ProductImages from '@/components/Product/ProductMedia';
import Div from '@/shared/Div';
import getSingleString from '@/utils/string';
import { NextPageContext } from 'next';
import dlv from 'dlv';
import Map from '@/components/Map';
import {
  EFL_CART_ID,
  EFL_PINCODE,
  EFL_RECENTLY_VIEWED_PRODUCTS,
  EXISTING_PREXO_DETAILS,
  NOT_FOUND,
  PREXO_PRODUCT_NAME,
  RECENTLY_VIEWED_PRODUCTS_COUNT,
} from '@/constants/app';
import React, { Fragment, useCallback, useMemo, useEffect, useState, useRef } from 'react';
import ProductTitle from '@/components/Product/ProductTitle';
import { MapType } from '@/types';
import ProductDescription from '@/components/Product/ProductDescription';
import ProductName from '@/components/Product/ProductName';
import ServiceBenefit from '@/components/Product/ServiceBenefit';
import AddToCart from '@/components/Product/AddToCart';
import ListWithIcon from '@/components/Product/ListWithIcon';
import ProductVariants from '@/components/Product/ProductVariants';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import LocalStorage from '@/utils/localStorage';
import { useRouter } from 'next/router';
import Button from '@/shared/Button';
import useRoutingLoader from '@/hooks/useRoutingLoader';
import ssrErrorHandler from '@/errors/ssrErrorHandler';
import ExistingUser from '@/shared/crisp/ExistingUser';
import Cookies from '@/utils/cookie';
import PurificationStages from '@/shared/crisp/PurificationStages';
import { getCategoryURL, getSubCategoryURL } from '@/utils/urls';
import ExchangeProduct from '@/components/Product/ExchangeProduct';
import { useEcomProductContext } from '@/context/ecomProductContext';
import { ECOM_CART_URL, ECOM_DEFAULT_QUANTITY, ECOM_HOME_URL, SC_CODE } from '@/configs/constants';
import dynamic from 'next/dynamic';
import is from '@/utils/is';
import { isInCart, isOutOfStock } from '@/utils/product';
import useEcomProduct from '@/hooks/useEcomProduct';
import useToast from '@/hooks/useToast';
import Head from 'next/head';
import useEcomGTM from '@/hooks/useEcomGTM';
import { EcomGTMEventNames } from '@/types/ecomGTM';
import ProductDemo from '@/components/Product/ProductDemo';
import useOnMount from '@/hooks/useOnMount';
import { populatePdpEcomGTM } from '@/utils/gtmUtils';
import { Flags } from '@/types/pdp';
import { useProfileContext } from '@/context/profileContext';
import { CART_NOT_FOUND_FOR_GIVEN_ID, INVENTORY_NOT_AVAILABLE } from '@/configs/apiErrorMessages';
import { useCartActions } from '@/hooks/useCartActions';
import useRemoveQueryParam from '@/hooks/useRemoveQueryParam';

const Delivery = dynamic(
  () => import(/* webpackChunkName: "pdp-lazy-chunks" */ '@/components/Product/Delivery'),
  {
    ssr: false,
  },
);

const SizingAndFitting = dynamic(
  () => import(/* webpackChunkName: "pdp-lazy-chunks" */ '@/components/Product/SizingAndFitting'),
  {
    ssr: false,
  },
);

const APlusContent = dynamic(
  () => import(/* webpackChunkName: "pdp-lazy-chunks" */ '@/components/Product/APlusContent'),
  {
    ssr: false,
  },
);

const Faqs = dynamic(
  () => import(/* webpackChunkName: "pdp-ssr-lazy-chunks" */ '@/shared/crisp/Accordion'),
);

const ProductSpecifications = dynamic(
  () =>
    import(
      /* webpackChunkName: "pdp-ssr-lazy-chunks" */ '@/components/Product/ProductSpecifications'
    ),
);

const PRODUCT_COMPONENT_MAP: MapType = {
  ProductImages,
  ProductTitle,
  ProductDescription,
  Delivery,
  ProductSpecifications,
  Faqs,
  ServiceBenefits: ServiceBenefit,
  AlsoIncluded: ListWithIcon,
  AddToCart,
  ProductVariants,
  PurificationStages,
  SignIn: ExistingUser,
  SizingAndFitting,
  ExchangeProduct,
  APlusContent,
  ProductDemo,
};

export default function ProductDetailsPage({
  productDetail,
  pageLayout,
  componentProps,
  category,
  subCategory,
  seo,
  flags,
  requsetExpertLink,
  scCode,
}: any) {
  const productPrice = (...args: any) => getSingleProductPrices.apply(null, args);
  const { promisifyTrigger: promisifyTriggerPrice, isSuccess } = useTriggerRequest({
    fetch: productPrice,
  });

  const fetch = (...args: any) => addToCart.apply(null, args);
  const { promisifyTrigger, isError, error, reset } = useTriggerRequest({ fetch });
  const router = useRouter();
  const { routing, setRouting } = useRoutingLoader();
  const [price, setPrice] = useState(productDetail?.price);
  const [priceLoaded, setPriceLoaded] = useState(false);
  const {
    state: { isLoggedIn },
  } = useProfileContext();

  const { removeItemfn } = useCartActions();

  const maxAddToCartRetries = useRef(2);
  const {
    productId = '',
    id,
    materialId,
    background,
    plainName,
    image,
    originalImage,
  } = productDetail;
  const quantity = is.null(productDetail.quantity)
    ? ECOM_DEFAULT_QUANTITY.min
    : productDetail.quantity?.min;
  const { state: ecomProductState, dispatch: ecomProductDispatch } = useEcomProductContext();
  const { triggerDefault } = useToast();
  const { commercial, saleable } = flags as Flags;
  const { fireEcomGTMEvent } = useEcomGTM();

  const [selectedPrexo, setSelectedPrexo] = useState<{
    categoryName: string;
    value: number;
  } | null>(null);

  useOnMount(() => {
    if (scCode) {
      LocalStorage.set(SC_CODE, scCode, 12 * 60 * 60 * 1000);
    }
    fireEcomGTMEvent(EcomGTMEventNames.PDP.PRODUCT_INFORMATION, {
      product_title: plainName,
      product_material_id: materialId,
      productMRP: price?.mrp,
      productMOP: price?.mop,
      deal_tag_Name: dlv(componentProps, 'AddToCart.dealTag.label'),
      coupon_count: price?.coupons?.length,
      is_ncemi_available: dlv(price, 'ncemi.eligible'),
      is_variant_available: is.not.empty.array(
        dlv(componentProps, 'ProductVariants.variants.products'),
      ),
      is_discounted_price_available: price?.mrp !== price?.mop,
      is_prexo_available: dlv(price, 'prexo.eligible'),
    });
  });

  useEffect(() => {
    if (materialId) {
      ecomProductDispatch.updateCurrentProduct(productDetail);
    }
    setPrice(productDetail.price);
  }, [materialId]);

  const product = useEcomProduct(materialId);
  const isProductInCart = useMemo(
    () => isInCart(ecomProductState.inCartItems, productId),
    [productId, ecomProductState.inCartItems],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (isLoggedIn) {
        const { data } = (await promisifyTriggerPrice(materialId)) || {};
        if (data) {
          setPrice(data?.data?.[0]);
          setPriceLoaded(true);
        }
      }
    };

    fetchData();
  }, [isLoggedIn]);

  const isProductOutOfStock = isOutOfStock(product);

  const setRecentlyViewedProducts = (id: string) => {
    const recentlyViewedProducts = Cookies.get(EFL_RECENTLY_VIEWED_PRODUCTS)?.split(',') || [];
    if (recentlyViewedProducts.length === 0) {
      Cookies.set(EFL_RECENTLY_VIEWED_PRODUCTS, id, 30);
      return;
    }
    if (!recentlyViewedProducts?.includes(String(id))) {
      recentlyViewedProducts.unshift(id);
      if (recentlyViewedProducts.length > RECENTLY_VIEWED_PRODUCTS_COUNT) {
        recentlyViewedProducts?.pop();
      }
      Cookies.set(EFL_RECENTLY_VIEWED_PRODUCTS, recentlyViewedProducts.join(','), 30);
    }
  };

  useEffect(() => {
    (async () => {
      const message = error?.error;
      if (isError && message) {
        reset();
        if (message === CART_NOT_FOUND_FOR_GIVEN_ID && maxAddToCartRetries.current > 0) {
          maxAddToCartRetries.current--;
          LocalStorage.remove(EFL_CART_ID);
          handleAddToCart();
        } else if (message === INVENTORY_NOT_AVAILABLE) {
          const promises = ecomProductState?.inCartItems?.map((cartItemId) =>
            removeItemfn(cartItemId),
          );
          await Promise.all(promises);
          handleAddToCart();
        } else {
          triggerDefault({ message });
          setRouting(false);
        }
      }
    })();
  }, [isError, error]);

  useEffect(() => {
    if (id) {
      setRecentlyViewedProducts(id);
    }
  }, [id]);

  const handleAddToCart = useCallback(async () => {
    const { mrp = '', sellingPrice = '' } = price;
    const payload: any[] = [];
    if (!isProductInCart) {
      payload.push({
        itemId: productId,
        quantity,
        sellingPrice,
        type: 'product',
        properties: {
          name: plainName,
          img: image,
          mrp,
        },
      });
    }

    const { eligible, id, prexoDisc, prexoValues } = dlv(product, 'prexo') ?? {};

    if (eligible && id && selectedPrexo) {
      const prexoPayload: any = {
        itemId: id,
        relatesTo: productId,
        type: 'prexo',
        sellingPrice: selectedPrexo.value,
        quantity: 1,
        properties: {
          name: PREXO_PRODUCT_NAME[category.type],
          mrp: selectedPrexo.value,
          exchangeCategoryName: selectedPrexo.categoryName,
        },
      };
      payload.push(prexoPayload);
    }

    setRouting(true);
    const responseData = await promisifyTrigger(payload);
    const cartId = dlv(responseData, 'data.data.data.cart_id', null);
    if (cartId) {
      LocalStorage.set(EFL_CART_ID, cartId);
      fireGTMEvent();
      router.push(ECOM_CART_URL).then(() => setRouting(false));
    }
    setRouting(false);
  }, [
    image,
    plainName,
    price,
    product,
    productId,
    promisifyTrigger,
    quantity,
    router,
    setRouting,
    category.type,
    selectedPrexo,
  ]);

  useRemoveQueryParam([SC_CODE], true);

  const fireGTMEvent = () => {
    const { eligible, id } = dlv(product, 'prexo') ?? {};
    const { coupon, applicableDisc } = dlv(product, 'appliedCoupon') ?? {};
    const ecommerce = populatePdpEcomGTM(productDetail, category, product);
    fireEcomGTMEvent(
      EcomGTMEventNames.PDP.ADD_TO_CART,
      {
        item_subcategory: subCategory?.name,
        in_stock: 'yes',
        exchange_applied: !!(eligible && id),
        productName: plainName, //item_name
        deliveryPincode: LocalStorage.get(EFL_PINCODE) ?? '',
        is_prexo_selected: !!(eligible && id),
        couponApplied: !!(coupon && applicableDisc),
        couponCode: coupon ?? '',
        ecommerce: populatePdpEcomGTM(productDetail, category, product),
        ...(ecommerce?.items?.[0] ?? {}),
      },
      {
        webengage: {
          fire: true,
          isDirect: true,
        },
      },
    );
  };

  const renderAddToCartCTA = useCallback(
    ({ theme, className, dataTestId, ...rest }: any) => {
      const props = {
        theme,
        className,
        dataTestId,
        isLoading: routing,
        ...rest,
      };

      if (commercial) {
        return (
          <Button {...props} target="_blank" href={requsetExpertLink}>
            Request Expert
          </Button>
        );
      }

      if (isProductOutOfStock || !saleable) {
        return (
          <Button {...props} disabled>
            Out of stock
          </Button>
        );
      }

      if (isProductInCart)
        return (
          <Button {...props} href={ECOM_CART_URL}>
            Go to cart
          </Button>
        );
      return (
        <Button {...props} onClick={handleAddToCart}>
          Add to cart
        </Button>
      );
    },
    [handleAddToCart, routing, isProductInCart, isProductOutOfStock, saleable, commercial],
  );
  const extendedComponentProps = useMemo(() => {
    // Get values from localStorage

    return {
      ...componentProps,
      AddToCart: {
        ...componentProps.AddToCart,
        price,
        renderAddToCartCTA,
        materialId,
        flags,
        priceLoaded,
      },
      ExchangeProduct: {
        ...componentProps.ExchangeProduct,
        price,
        materialId,
        isProductOutOfStock,
        isProductInCart,
        flags,
        category,
        handleAddToCart,
        selectedPrexo,
        setSelectedPrexo,
      },
      AlsoIncluded: {
        ...componentProps.AlsoIncluded,
        className: 'mt-s9 md:mt-s10',
      },
      Delivery: {
        ...componentProps.Delivery,
        installation: price.installation,
        shipping: price.shipping,
        flags,
      },
      SignIn: {
        ...componentProps.SignIn,
        flags,
      },
      ProductImages: {
        ...componentProps.ProductImages,
        plainName,
      },
      ProductDemo: {
        ...(componentProps.ProductDemo ?? {}),
      },
    };
  }, [
    componentProps,
    renderAddToCartCTA,
    price,
    materialId,
    isProductOutOfStock,
    isProductInCart,
    flags,
    plainName,
    handleAddToCart,
    selectedPrexo,
    setSelectedPrexo,
  ]);

  const render = useCallback(
    (comp: string, index: number) => {
      const Comp = PRODUCT_COMPONENT_MAP[comp] ?? Fragment;
      const compProps = extendedComponentProps[comp] ?? {};
      return <Comp key={comp + index} {...compProps} />;
    },
    [extendedComponentProps],
  );

  const dataSet = useMemo(() => {
    const breadcrumbs = [{ id: 1, text: 'Home', link: ECOM_HOME_URL }];

    if (category) {
      breadcrumbs.push({
        id: 2,
        text: category.name,
        link: getCategoryURL(category.categoryId),
      });
    }

    if (category && subCategory) {
      breadcrumbs.push({
        id: 3,
        text: subCategory.name,
        link: getSubCategoryURL(category.categoryId, subCategory.subCategoryId),
      });
    }

    breadcrumbs.push({ id: 4, text: productDetail.title, link: '' });

    return breadcrumbs;
  }, [category, subCategory, productDetail.title]);

  const { title } = extendedComponentProps.ProductName;

  return (
    <PageLayout hideFooterIcons seo={seo}>
      <Head>
        <link rel="preload" as="image" href={originalImage} />
      </Head>
      {/* <Container className="!px-0  font-primary"> */}
      <ProductName
        title={title}
        price={price}
        renderAddToCartCTA={renderAddToCartCTA}
        commercial={commercial}
      />
      <Div className="flex flex-wrap items-start">
        <Div
          className="pb-s5 md:pb-s7 md:sticky  lg:top-42.5 w-full md:md:w-7/12"
          style={{ background }}
        >
          <Breadcrumb dataSet={dataSet} className="py-s4 px-s5 md:py-s5 md:px-s6" />
          <Map list={pageLayout.column.left} render={render} />
        </Div>
        <Map
          className="flex flex-col p-s5 lg:p-s8 w-full md:w-5/12"
          list={pageLayout.column.right}
          render={render}
        />
      </Div>
      <Map list={pageLayout.content} render={render} />
      {/* </Container> */}
    </PageLayout>
  );
}

export async function getServerSideProps(nextPageContext: NextPageContext) {
  const { query, req } = nextPageContext;
  const { slug } = query;
  const cookie = req?.headers?.cookie || '';
  const materialId = getSingleString(slug?.[0]);
  const scCode = query?.scCode ?? null;

  if (materialId) {
    try {
      const [productDetail, { data: productPrice }] = await Promise.all([
        getProductDetail(materialId),
        getSingleProductPrices(materialId, cookie),
      ]);

      const {
        seo,
        media,
        mediaBackground = '#FFFFFF',
        description,
        layout,
        title,
        plainName,
        name,
        included,
        faqs,
        serviceBenefit,
        productId,
        specifications,
        variants,
        dealTag,
        purificationStages,
        signIn,
        sizingAndFitting,
        category,
        subCategories,
        quantity,
        aPlusContent = null,
        flags,
        brand,
        requsetExpertLink,
        expertGuidanceDemo = null,
      } = productDetail?.attributes ?? {};
      const pageLayout = dlv(layout, 'data.attributes.config', {
        column: { left: [], right: [] },
        content: [],
      });

      const serviceBenefits = dlv(serviceBenefit, 'data.attributes.benefits', {
        title: '',
        list: [],
      });

      const price = dlv(productPrice, 'data.0', {});

      const componentProps = {
        ProductName: { title },
        ProductImages: { media: media?.data },
        ProductTitle: {
          productName: name,
        },
        ProductDescription: description,
        Delivery: {
          title: 'Delivery & Installation',
          productId,
          materialId,
        },
        Faqs: faqs || null,
        ProductSpecifications: specifications || null,
        ServiceBenefits: serviceBenefits || null,
        AlsoIncluded: included || null,
        AddToCart: {
          dealTag: dlv(dealTag, 'data.attributes', null),
        },
        ExchangeProduct: { productId },
        ProductVariants: { variants, materialId },
        PurificationStages: purificationStages,
        SizingAndFitting: sizingAndFitting || null,
        SignIn: signIn || null,
        APlusContent: aPlusContent,
        ProductDemo: expertGuidanceDemo,
      };

      let subCategory = null;

      if (subCategories?.data && subCategories.data?.length > 0) {
        const { name, subCategoryId } = dlv(subCategories, 'data.0.attributes', {});
        subCategory = { name, subCategoryId };
      }

      let productCategory = null;
      if (category.data) {
        const { name, categoryId, type } = dlv(category, 'data.attributes', {});
        productCategory = {
          name,
          categoryId,
          type,
        };
      }
      // First PDP image
      const { formats, url } = dlv(media, 'data.0.attributes', {});

      if (is.null(price.coupons) || !price.coupons) price.coupons = [];

      return {
        props: {
          productDetail: {
            title,
            id: productDetail?.id,
            plainName,
            productId,
            quantity,
            materialId,
            background: mediaBackground,
            price,
            image: dlv(formats, 'thumbnail.url', url),
            originalImage: url,
            brand,
            // First PDP Image
          },
          pageLayout,
          componentProps,
          category: productCategory,
          subCategory,
          seo,
          flags: flags || {},
          requsetExpertLink,
          scCode,
        },
      };
    } catch (error) {
      return ssrErrorHandler(error, nextPageContext);
    }
  }

  return NOT_FOUND;
}
